import styled from 'styled-components';
import { useState } from 'react';

import { Button, SEO, SoftButtonLink } from 'components';
import {
    CategoryTrendsBrandsGoogleWidget,
    CategoryTrendsBrandsPopularityWidget,
    CategoryTrendsGoogleWidget,
    CategoryTrendsPopularityWidget,
} from 'components/CategoryTrendsWidget';
import {
    SectionCard,
    SectionCardH1,
    SectionCardHeading,
    SectionCardIcon,
    SectionCardSubH1,
} from 'components/SectionCard';
import {
    useCurrentCountry,
    useDataSourceVersion,
    useFetchSpotlights,
    useFetchWatchlists,
} from 'utils/hooks';
import { Input as DSInput } from '@/ui/design-system/components/ui/input';
import { Textarea as DSTextarea } from '@/ui/design-system/components/ui/textarea';
import { FiEye } from 'react-icons/fi';
import { GiFactory } from 'react-icons/gi'; // TODO: this is supposed to be a custom icon
import HomeCommandCenter from 'components/AiAssistant/HomeCommandCenter';
import IndustryTiles from 'components/IndustryTiles';
import { useIndustries } from 'utils/hooks/useUserIndustries';
import Link from 'components/Link';
import { MdOutlineSchool } from 'react-icons/md';
import NewWatchlist from 'components/Button/NewWatchlist';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import SpotlightTiles from 'components/Spotlights';
import Stack from 'components/Stack';
import { TIKTOK_COUNTRIES } from '@/config/constants';
import VertLine from 'components/VertLine';
import WatchlistTiles from 'components/WatchlistTiles';
import { useFlagActions } from 'utils/hooks/flags';
import { useMutation } from 'react-query';

function IndustryTilesSection() {
    const country = useCurrentCountry();
    const { googleVersion } = useDataSourceVersion();
    const maybeSourceDataUpdated =
        googleVersion?.data?.dbDates?.[country?.text_id]?.endDate || null;
    const { data, isLoading, isError, refetch } = useIndustries();

    return (
        <SectionCard space="0px" css="padding: 0px;" id="industry-tiles">
            <SectionCardHeading css="padding: 14px 18px 0px;">
                <SectionCardIcon Icon={GiFactory} />
                <SectionCardH1>Industries.</SectionCardH1>
                <SectionCardSubH1>Begin your search here.</SectionCardSubH1>
                <SourceDataUpdated>
                    Data update: {maybeSourceDataUpdated}
                </SourceDataUpdated>
            </SectionCardHeading>
            {isLoading ? (
                <IndustryTiles.GridScroll>
                    <IndustryTiles.Tile isLoading={true} />
                </IndustryTiles.GridScroll>
            ) : isError ? (
                <div css="height: 150px; display: flex; flex-direction: column; align-items: center;">
                    <div
                        css={`
                            margin-bottom: ${({ theme }) => theme.space_4}px;
                        `}
                    >
                        Could not load the industries.
                    </div>
                    <Button intent="secondary" onClick={() => refetch()}>
                        Retry
                    </Button>
                </div>
            ) : (
                <IndustryTiles.GridScroll>
                    {(data || []).map(({ id, name, to, image }) => (
                        <IndustryTiles.Tile
                            key={id}
                            name={name}
                            image={image}
                            to={to}
                            as={Link}
                        />
                    ))}
                </IndustryTiles.GridScroll>
            )}
        </SectionCard>
    );
}

function DashboardChooseIndustry() {
    const country = useCurrentCountry();
    const { data: spotlights, ...spotlightsQuery } = useFetchSpotlights();
    const { data: watchlists, ...watchlistsQuery } = useFetchWatchlists();
    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);

    return (
        <>
            <SEO />
            <PagePadding>
                <PageBar />
                <Stack $space="28px" id="homepage">
                    <HomeCommandCenter />
                    <IndustryTilesSection />
                    {isNotTikTokMarket ? (
                        <CategoryTrendsGoogleWidget />
                    ) : (
                        <CategoryTrendsPopularityWidget />
                    )}
                    {isNotTikTokMarket ? (
                        <CategoryTrendsBrandsGoogleWidget />
                    ) : (
                        <CategoryTrendsBrandsPopularityWidget />
                    )}
                    <SectionCard
                        space="0px"
                        css="padding: 0px;"
                        id="watchlist-tiles"
                    >
                        <SectionCardHeading css="padding: 14px 18px 0;">
                            <SectionCardIcon Icon={FiEye} />
                            <SectionCardH1>My Watchlist.</SectionCardH1>
                            <SectionCardSubH1>
                                Quick access to your lists.
                            </SectionCardSubH1>
                            <Stack
                                $space=".375rem"
                                $direction="row"
                                $wrap="false"
                                css={`
                                    display: flex;
                                    align-items: center;
                                    margin-left: auto;
                                `}
                            >
                                <NewWatchlist />
                                <VertLine />
                                <SoftButtonLink to="/watchlist">
                                    All Watchlists
                                </SoftButtonLink>
                            </Stack>
                        </SectionCardHeading>
                        <WatchlistTiles.Tiles
                            tiles={
                                watchlists
                                    ? Object.values(watchlists).map(
                                          ({ id, name }) => ({
                                              key: id,
                                              title: name,
                                              to: `/watchlist/${id}`,
                                              showIcon: false,
                                          }),
                                      )
                                    : null
                            }
                            status={watchlistsQuery.status}
                            Grid={WatchlistTiles.GridScroll}
                            css={`
                                font-size: ${({ theme }) => theme.fontSize_3};
                            `}
                        />
                    </SectionCard>
                    <SectionCard
                        css="padding: 14px 18px 18px;"
                        id="spotlight-tiles"
                    >
                        <SectionCardHeading>
                            <SectionCardIcon Icon={MdOutlineSchool} />
                            <SectionCardH1>Spotlight.</SectionCardH1>
                            <SectionCardSubH1>
                                Spate's latest reports.
                            </SectionCardSubH1>
                            <SoftButtonLink
                                size="sm"
                                className="!ml-auto !h-auto !py-1.5 !px-3 !text-xs"
                                to="/spotlights"
                            >
                                All Spotlights
                            </SoftButtonLink>
                        </SectionCardHeading>
                        <SpotlightTiles
                            spotlights={
                                spotlights ? spotlights.slice(0, 2) : null
                            }
                            status={spotlightsQuery.status}
                        />
                    </SectionCard>
                    <SectionCard css="padding-top: 24px; padding-bottom: 24px;">
                        <SuggestForm />
                    </SectionCard>
                </Stack>
            </PagePadding>
        </>
    );
}

function SuggestForm() {
    const [state, setState] = useState({ suggestion: '', comment: '' });
    const { flagNoResultsQuery } = useFlagActions();
    const {
        mutateAsync: suggest,
        status,
        reset,
    } = useMutation(
        ({ suggestion, comment }) => flagNoResultsQuery(suggestion, comment),
        {
            onSuccess: () => setState({ suggestion: '', comment: '' }),
        },
    );

    const handleChange = (nextState) => {
        if (!['idle', 'loading'].includes(status)) {
            reset();
        }
        setState(nextState);
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                suggest({ ...state });
            }}
            css={`
                margin: 0 auto;
                max-width: 460px;

                p {
                    text-align: center;
                    font-family: inherit;
                    margin: 0px;
                    color: ${({ theme }) => theme.gray_3};
                }

                p:first-of-type {
                    font-weight: bold;
                    color: ${({ theme }) => theme.gray_2};
                }
            `}
        >
            <Stack css="display: flex; flex-direction: column;" $space="16px">
                <Stack $space="6px">
                    <p>Can't find a brand, industry or ingredient?</p>
                    <p>
                        Let us know and we'll try to include it in our next
                        update.
                    </p>
                </Stack>
                <DSInput
                    type="text"
                    placeholder="Type here a brand, industry or ingredient"
                    value={state.suggestion}
                    onChange={(e) =>
                        handleChange({ ...state, suggestion: e.target.value })
                    }
                    disabled={status === 'loading'}
                    required={true}
                />
                <DSTextarea
                    placeholder="Add a comment (optional)"
                    value={state.comment}
                    onChange={(e) =>
                        handleChange({ ...state, comment: e.target.value })
                    }
                    disabled={status === 'loading'}
                />
                <div css="text-align: center;">
                    <Button
                        type="submit"
                        disabled={status === 'loading'}
                        fetching={status === 'loading'}
                        className="min-w-[250px]"
                        mixpanelContext="SuggestButton"
                    >
                        Send Suggestion
                    </Button>
                </div>
            </Stack>
        </form>
    );
}

const SourceDataUpdated = styled.div`
    margin-left: auto;
    font-size: ${({ theme }) => theme.space_7}px;
    color: ${({ theme }) => theme.gray_4};
`;

export default DashboardChooseIndustry;
